import './Pager.css';
import { t } from '../copy/strings';
import { IconButton } from './IconButton';

export interface PagerProps {
  /** Página atual, base 1 (fora de 1…total é limitada). */
  page: number;
  /** Total de páginas. Com `total ≤ 1` o `Pager` não renderiza, salvo `showSingle`. */
  total: number;
  /** Recebe a página nova (sempre entre 1 e `total`). */
  onChange: (page: number) => void;
  /** Nome acessível do grupo; padrão "Páginas" (`pager.group`). */
  label?: string;
  /** Nome do botão ‹; padrão "Anteriores" (`pager.prev`). `ZoneList` usa "Zonas anteriores" (§8.4). */
  prevLabel?: string;
  /** Nome do botão ›; padrão "Próximas" (`pager.next`). `ZoneList` usa "Próximas zonas" (§8.4). */
  nextLabel?: string;
  /** Mostra "1/1" com as duas setas desabilitadas em vez de sumir (`RaceList`, §11.1). */
  showSingle?: boolean;
}

/**
 * Paginação compacta "‹ 1/3 ›" (§3.9 `Pager`). Os botões nunca são recriados ao trocar de página, então o foco fica no
 * botão usado; nas pontas ficam com `aria-disabled="true"` e continuam focáveis (§15.2.2 item 5). O texto "1/3" é
 * visual (`aria-hidden`), com a cópia `.sr-only` "Página 1 de 3" (§15.4.2). Some na TV (`.tv-hide`).
 */
export function Pager({ page, total, onChange, label, prevLabel, nextLabel, showSingle = false }: PagerProps) {
  const n = Math.max(1, Math.floor(Number.isFinite(total) ? total : 1));
  if (n <= 1 && !showSingle) return null;
  const p = Math.min(n, Math.max(1, Math.round(Number.isFinite(page) ? page : 1)));
  const first = p <= 1;
  const last = p >= n;
  return (
    // biome-ignore lint/a11y/useSemanticElements: §3.9 e §15.4.2 pedem role="group"; <fieldset> exige <legend> e não aceita inline-flex de forma confiável
    <div class="pager tv-hide" role="group" aria-label={label ?? t('pager.group')} data-testid="pager">
      <IconButton
        icon="chevron-left"
        label={prevLabel ?? t('pager.prev')}
        class="pager__prev"
        disabled={first}
        focusableWhenDisabled
        onClick={() => onChange(p - 1)}
      />
      <span class="pager__label">
        <span aria-hidden="true">{t('pager.label', { i: p, n })}</span>
        <span class="sr-only">{t('pager.page.sr', { i: p, n })}</span>
      </span>
      <IconButton
        icon="chevron-right"
        label={nextLabel ?? t('pager.next')}
        class="pager__next"
        disabled={last}
        focusableWhenDisabled
        onClick={() => onChange(p + 1)}
      />
    </div>
  );
}
